import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Camera, X, Loader2, Check, AlertTriangle, FileText, Truck } from 'lucide-react';
import { portalService, RespostaDoChecklist } from '../../services/portal';
import { Veiculo } from '../../types';
import { prepararImagemParaEnvio } from '../../utils/imagem';

/**
 * A conferência do veículo antes de a viagem sair.
 *
 * É a lista de papel que a frota já usava. A diferença de fazer aqui é que ela
 * deixa de ser ritual: sem checklist a viagem não inicia, e o que o motorista
 * reprovou chega ao gestor no mesmo instante, com foto.
 *
 * A tela assume o motorista em pé ao lado do caminhão, com uma mão no celular:
 * os itens vêm na ordem da volta que se dá no veículo, os botões são grandes o
 * bastante para o polegar, e o progresso fica sempre à vista — numa lista de 26
 * linhas, não saber quanto falta é o que faz desistir no meio.
 */

/** Mesmo teto do servidor. */
const MAX_FOTOS = 10;

const ChecklistDeViagem: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const viagemId = Number(id);

  const [respostas, setRespostas] = useState<Record<string, { ok: boolean; observacao: string }>>({});
  const [fotos, setFotos] = useState<Array<{ nome: string; caminho: string }>>([]);
  const [observacoes, setObservacoes] = useState('');
  const [enviandoFoto, setEnviandoFoto] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const inputCamera = useRef<HTMLInputElement>(null);
  const filaDeConsultas = useQueryClient();

  // O catálogo mora no servidor: se a tela guardasse cópia, um item novo só
  // apareceria para quem atualizasse o aplicativo.
  const { data: catalogo, isLoading } = useQuery({
    queryKey: ['portal', 'checklist', 'itens'],
    queryFn: portalService.getItensDoChecklist,
  });

  const itens = useMemo(() => catalogo?.data ?? [], [catalogo]);

  // O veiculo e escolhido aqui, e nao no iniciar: o checklist e a conferencia
  // de um carro, e trocar depois dele deixaria checklist e km de saida falando
  // de veiculos diferentes. Vem marcado o que a gestao planejou.
  const { data: respostaViagens } = useQuery({
    queryKey: ['portal', 'viagens', 'hoje'],
    queryFn: portalService.getViagensDeHoje,
  });
  const viagem = respostaViagens?.data?.find((v) => v.id === viagemId);

  const { data: respostaVeiculos } = useQuery({
    queryKey: ['portal', 'veiculos'],
    queryFn: portalService.getVeiculos,
  });

  // Os atribuidos a ele, mais o planejado — que a gestao pode ter escolhido
  // fora da lista dele e continua valendo.
  const opcoesDeVeiculo = useMemo(() => {
    const atribuidos: Veiculo[] = (respostaVeiculos?.data as Veiculo[]) ?? [];
    const planejado = viagem?.veiculo;
    return planejado && !atribuidos.some((v) => v.id === planejado.id) ? [planejado, ...atribuidos] : atribuidos;
  }, [respostaVeiculos, viagem]);

  const [veiculoId, setVeiculoId] = useState('');
  useEffect(() => {
    if (viagem?.veiculo_id != null && veiculoId === '') setVeiculoId(String(viagem.veiculo_id));
  }, [viagem, veiculoId]);

  const trocouDeVeiculo = viagem != null && veiculoId !== '' && veiculoId !== String(viagem.veiculo_id);

  const respondidos = itens.filter((i) => respostas[i.chave] !== undefined).length;
  const completo = itens.length > 0 && respondidos === itens.length;

  const marcar = (chave: string, ok: boolean) =>
    setRespostas((atuais) => ({
      ...atuais,
      // Trocar de "Não" para "OK" apaga a observação: ela descrevia um problema
      // que o motorista acabou de dizer que não existe.
      [chave]: { ok, observacao: ok ? '' : (atuais[chave]?.observacao ?? '') },
    }));

  const escreverObservacao = (chave: string, observacao: string) =>
    setRespostas((atuais) => ({ ...atuais, [chave]: { ok: atuais[chave]?.ok ?? false, observacao } }));

  const escolherFotos = async (lista: FileList | null) => {
    if (!lista || lista.length === 0) return;

    const espaco = MAX_FOTOS - fotos.length;
    if (espaco <= 0) {
      toast.error(`Máximo de ${MAX_FOTOS} fotos.`);
      return;
    }

    const aceitas = Array.from(lista).slice(0, espaco);
    setEnviandoFoto((n) => n + aceitas.length);

    for (const escolhida of aceitas) {
      try {
        // Foto de iPhone sai em HEIC, que o servidor recusa e o Chrome nem abre:
        // vira JPEG aqui, no próprio aparelho.
        const arquivo = await prepararImagemParaEnvio(escolhida);
        const resposta = await portalService.enviarFotoDoChecklist(arquivo);
        const caminho = resposta.data?.path ?? '';

        if (caminho) {
          setFotos((atuais) => [...atuais, { nome: arquivo.name, caminho }]);
        }
      } catch (erro: any) {
        toast.error(
          erro?.response?.data?.errors?.file?.[0] ?? `Não foi possível enviar ${escolhida.name}.`,
        );
      } finally {
        setEnviandoFoto((n) => n - 1);
      }
    }

    // Sem limpar, escolher o mesmo arquivo de novo não dispara o onChange.
    if (inputCamera.current) inputCamera.current.value = '';
  };

  const enviar = async () => {
    // As checagens acontecem aqui, e não só no servidor, porque o motorista
    // está de pé ao lado do caminhão: descobrir o que falta depois de um
    // 4G lento é o que faz ele desistir.
    const semResposta = itens.find((i) => respostas[i.chave] === undefined);
    if (semResposta) {
      toast.error(`Falta responder: ${semResposta.rotulo}.`);
      return;
    }

    const semMotivo = itens.find(
      (i) => respostas[i.chave]?.ok === false && !respostas[i.chave]?.observacao.trim(),
    );
    if (semMotivo) {
      toast.error(`Diga o que há de errado em: ${semMotivo.rotulo}.`);
      return;
    }

    if (fotos.length === 0) {
      toast.error('Tire pelo menos uma foto do veículo.');
      return;
    }

    setSalvando(true);

    try {
      const itensParaEnviar: RespostaDoChecklist[] = itens.map((i) => ({
        chave: i.chave,
        ok: respostas[i.chave].ok,
        observacao: respostas[i.chave].ok ? null : respostas[i.chave].observacao.trim(),
      }));

      await portalService.salvarChecklist(viagemId, {
        itens: itensParaEnviar,
        fotos: fotos.map((f) => f.caminho),
        observacoes: observacoes.trim() || null,
        ...(veiculoId ? { veiculo_id: Number(veiculoId) } : {}),
      });

      // Derruba a lista de hoje antes de voltar. O aplicativo guarda consulta
      // por 5 minutos, e sem isto o portal serve a versão de antes — dizendo
      // que o checklist não foi feito. O motorista refaria a conferência
      // inteira, e a segunda tentativa levaria 409.
      await filaDeConsultas.invalidateQueries({ queryKey: ['portal', 'viagens', 'hoje'] });

      toast.success('Checklist enviado. Agora informe a quilometragem para iniciar a viagem.');
      navigate('/motorista');
    } catch (erro: any) {
      const erros = erro?.response?.data?.errors;
      const primeiro = erros ? (Object.values(erros)[0] as string[])?.[0] : null;
      toast.error(primeiro ?? erro?.response?.data?.message ?? 'Não foi possível enviar o checklist.');
      setSalvando(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50 pb-28">
      <header className="bg-white border-b border-secondary-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/motorista')}
            className="p-2 -ml-2 rounded-lg text-secondary-500 hover:bg-secondary-100"
            aria-label="Voltar"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-secondary-900">Checklist de saída</h1>
            {/* O progresso fica no cabeçalho, sempre à vista: numa lista de 26
                linhas, não saber quanto falta é o que faz desistir no meio. */}
            <p className="text-xs text-secondary-500">
              {respondidos} de {itens.length} itens conferidos
            </p>
          </div>
        </div>
        <div className="h-1 bg-secondary-100">
          <div
            className="h-1 bg-primary-600 transition-all"
            style={{ width: itens.length ? `${(respondidos / itens.length) * 100}%` : '0%' }}
          />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2 text-sm text-amber-900">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-500" />
          <p>
            Confira o veículo item por item. Marcando <b>Não</b>, diga o que há de errado — é o que o gestor
            usa para decidir se o caminhão sai. Depois de enviar você não consegue alterar.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-secondary-200 p-4">
          <label htmlFor="veiculo_id" className="flex items-center gap-2 text-sm font-medium text-secondary-700 mb-1">
            <Truck className="w-4 h-4 text-secondary-400" /> Veículo desta viagem
          </label>
          <select
            id="veiculo_id"
            value={veiculoId}
            onChange={(e) => setVeiculoId(e.target.value)}
            className="block w-full rounded-lg border-secondary-300 py-3 text-base shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            {veiculoId === '' && <option value="">Carregando...</option>}
            {opcoesDeVeiculo.map((v) => (
              <option key={v.id} value={v.id}>
                {v.placa} — {v.marca} {v.modelo}
                {viagem && v.id === viagem.veiculo_id ? ' (planejado)' : ''}
              </option>
            ))}
          </select>
          <p className={`mt-1 text-xs ${trocouDeVeiculo ? 'text-amber-700' : 'text-secondary-500'}`}>
            {trocouDeVeiculo
              ? 'A viagem vai sair com este veículo, e não com o planejado. Confira este carro no checklist abaixo.'
              : 'Vai sair com outro veículo seu? Troque aqui, antes de conferir.'}
          </p>
        </div>

        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          </div>
        )}

        <div className="bg-white rounded-xl border border-secondary-200 divide-y divide-secondary-100">
          {itens.map((item, indice) => {
            const resposta = respostas[item.chave];

            return (
              <div key={item.chave} className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-secondary-800">
                    <span className="text-secondary-400 mr-2">{indice + 1}</span>
                    {item.rotulo}
                  </span>

                  {/* Dois botões grandes em vez de um seletor: o motorista está
                      de pé, com uma mão no celular e a outra segurando o quê
                      for. */}
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      type="button"
                      aria-pressed={resposta?.ok === true}
                      aria-label={`${item.rotulo}: OK`}
                      onClick={() => marcar(item.chave, true)}
                      className={`w-14 h-10 rounded-lg text-sm font-semibold border ${
                        resposta?.ok === true
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-secondary-600 border-secondary-300'
                      }`}
                    >
                      OK
                    </button>
                    <button
                      type="button"
                      aria-pressed={resposta?.ok === false}
                      aria-label={`${item.rotulo}: Não`}
                      onClick={() => marcar(item.chave, false)}
                      className={`w-14 h-10 rounded-lg text-sm font-semibold border ${
                        resposta?.ok === false
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-secondary-600 border-secondary-300'
                      }`}
                    >
                      Não
                    </button>
                  </div>
                </div>

                {resposta?.ok === false && (
                  <input
                    aria-label={`O que há de errado em ${item.rotulo}`}
                    value={resposta.observacao}
                    onChange={(e) => escreverObservacao(item.chave, e.target.value)}
                    maxLength={200}
                    placeholder="O que há de errado? Ex.: nível abaixo do mínimo"
                    className="mt-3 block w-full rounded-lg border-secondary-300 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-xl border border-secondary-200 p-4 space-y-3">
          <h2 className="text-sm font-medium text-secondary-700">
            Fotos do veículo <span className="text-red-500">*</span>
          </h2>

          {fotos.length === 0 && (
            <p className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-900">
              Pelo menos uma foto. Se marcou algum item como <b>Não</b>, fotografe o problema — a foto poupa
              a viagem de alguém até o pátio para ver.
            </p>
          )}

          {fotos.length > 0 && (
            <ul className="space-y-2">
              {fotos.map((foto) => (
                <li
                  key={foto.caminho}
                  className="flex items-center gap-3 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-3"
                >
                  <FileText className="w-5 h-5 text-secondary-400 flex-shrink-0" />
                  <span className="flex-1 min-w-0 text-sm text-secondary-700 truncate">{foto.nome}</span>
                  <button
                    type="button"
                    onClick={() => setFotos((atuais) => atuais.filter((f) => f.caminho !== foto.caminho))}
                    className="p-1 rounded text-secondary-400 hover:text-red-600"
                    aria-label={`Remover ${foto.nome}`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {enviandoFoto > 0 && (
            <div className="flex items-center gap-2 text-sm text-secondary-600">
              <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
              Enviando {enviandoFoto} foto(s)...
            </div>
          )}

          {fotos.length < MAX_FOTOS && (
            <button
              type="button"
              onClick={() => inputCamera.current?.click()}
              className="w-full flex flex-col items-center gap-1 rounded-lg border-2 border-dashed border-secondary-300 py-5 text-secondary-600 hover:border-primary-400 hover:text-primary-600"
            >
              <Camera className="w-6 h-6" />
              <span className="text-sm font-medium">Tirar foto</span>
            </button>
          )}

          <input
            ref={inputCamera}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => escolherFotos(e.target.files)}
          />
        </div>

        <div className="bg-white rounded-xl border border-secondary-200 p-4">
          <label htmlFor="observacoes" className="block text-sm font-medium text-secondary-700 mb-1">
            Observações
          </label>
          <textarea
            id="observacoes"
            rows={3}
            maxLength={500}
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Algo que o gestor precise saber"
            className="block w-full rounded-lg border-secondary-300 py-3 text-base shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>
      </main>

      {/* O botão fica fixo no rodapé: com 26 itens a página é longa, e rolar
          até o fim para descobrir que falta um item é a pior forma de saber. */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-secondary-200 px-4 py-3">
        <div className="max-w-2xl mx-auto">
          <button
            type="button"
            onClick={enviar}
            disabled={salvando || enviandoFoto > 0}
            className="w-full rounded-lg bg-primary-600 py-4 text-base font-semibold text-white hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {salvando && <Loader2 className="w-4 h-4 animate-spin" />}
            {completo && fotos.length > 0 && !salvando && <Check className="w-5 h-5" />}
            {/* O texto dentro de um elemento, e nao solto ao lado do icone: o
                icone entra ANTES dele, e se algo de fora (tradutor do
                navegador) trocar um texto solto, o React perde a referencia e
                a tela inteira cai. */}
            <span>{salvando ? 'Enviando...' : 'Enviar checklist'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChecklistDeViagem;
